import { COPY, PORTFOLIO, STORAGE_LANG, type Lang } from "./copy";

const SUPPORTED: readonly Lang[] = ["en", "zh"];

export function detectLang(): Lang {
  const stored = localStorage.getItem(STORAGE_LANG);
  if (stored === "en" || stored === "zh") return stored;
  return "en";
}

function setText(sel: string, text: string): void {
  document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
    el.textContent = text;
  });
}

function setHtml(sel: string, html: string): void {
  document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
    el.innerHTML = html;
  });
}

function renderPortfolio(lang: Lang): void {
  const list = document.getElementById("portfolio-list");
  if (!list) return;

  list.replaceChildren(
    ...PORTFOLIO.map((item, index) => {
      const row = document.createElement(item.href ? "a" : "div");
      row.className = "folio-row";
      row.dataset.marker = String(index);
      row.dataset.id = item.id;
      row.dataset.kind = item.kind;

      if (item.href) {
        const a = row as HTMLAnchorElement;
        a.href = item.href;
        a.rel = "noopener noreferrer";
        a.target = "_blank";
      }

      const idx = document.createElement("span");
      idx.className = "folio-idx mono";
      idx.textContent = String(index + 1).padStart(2, "0");

      const main = document.createElement("div");
      main.className = "folio-main";

      const name = document.createElement("span");
      name.className = "folio-name";
      name.textContent = lang === "zh" ? item.nameZh : item.nameEn;

      const role = document.createElement("span");
      role.className = "folio-role mono";
      role.textContent = lang === "zh" ? item.roleZh : item.roleEn;

      const title = document.createElement("div");
      title.className = "folio-title";
      title.append(name, role);

      const blurb = document.createElement("p");
      blurb.className = "folio-blurb";
      blurb.textContent = lang === "zh" ? item.blurbZh : item.blurbEn;

      main.append(title, blurb);

      const host = document.createElement("span");
      host.className = "folio-host mono";
      if (item.href) {
        try {
          host.textContent = new URL(item.href).host.replace(/^www\./, "");
        } catch {
          host.textContent = "";
        }
      } else {
        host.hidden = true;
      }

      // host lives with the text, not in a framed side column
      if (item.href) main.append(host);
      row.append(idx, main);
      return row;
    }),
  );
}

export function applyLang(lang: Lang): void {
  const copy = COPY[lang];
  document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";

  const page = document.body.dataset.page;
  if (!page || page === "home") {
    document.title = copy.title;
  }

  setHtml("[data-i18n=headline]", copy.headline);
  // lede may contain explicit line breaks
  setHtml(
    "[data-i18n=lede]",
    copy.lede
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<span>${line}</span>`)
      .join(""),
  );

  const map: Array<[string, string]> = [
    ["[data-i18n=kicker]", copy.kicker],
    ["[data-i18n=exploreHint]", copy.exploreHint],
    ["[data-i18n=helpTitle]", copy.helpTitle],
    ["[data-i18n=helpMove]", copy.helpMove],
    ["[data-i18n=helpLook]", copy.helpLook],
    ["[data-i18n=helpZoom]", copy.helpZoom],
    ["[data-i18n=helpZones]", copy.helpZones],
    ["[data-i18n=helpJump]", copy.helpJump],
    ["[data-i18n=portfolioLabel]", copy.portfolioLabel],
    ["[data-i18n=edgeLabel]", copy.edgeLabel],
    ["[data-i18n=status]", copy.status],
    ["[data-i18n=copyright]", copy.copyright],
    ["[data-i18n=navHome]", copy.navHome],
    ["[data-i18n=navLibrary]", copy.navLibrary],
    ["[data-i18n=navStatus]", copy.navStatus],
    ["[data-i18n=navMedia]", copy.navMedia],
    ["[data-i18n=libraryTitle]", copy.libraryTitle],
    ["[data-i18n=libraryLede]", copy.libraryLede],
    ["[data-i18n=statusTitle]", copy.statusTitle],
    ["[data-i18n=statusLede]", copy.statusLede],
    ["[data-i18n=mediaTitle]", copy.mediaTitle],
    ["[data-i18n=mediaLede]", copy.mediaLede],
  ];
  for (const [sel, text] of map) setText(sel, text);

  renderPortfolio(lang);

  const langBtn = document.querySelector<HTMLButtonElement>("#lang-toggle");
  if (langBtn) {
    langBtn.setAttribute("aria-label", copy.langAria);
    const indicator = langBtn.querySelector("#lang-indicator");
    if (indicator) indicator.textContent = lang === "zh" ? "En" : "中";
  }

  const themeBtn = document.querySelector<HTMLButtonElement>("#theme-toggle");
  if (themeBtn) themeBtn.setAttribute("aria-label", copy.themeAria);

  document.dispatchEvent(
    new CustomEvent("doorpost:lang", { detail: { lang } }),
  );
  localStorage.setItem(STORAGE_LANG, lang);
}

export function toggleLang(current: Lang): Lang {
  const next: Lang = current === "zh" ? "en" : "zh";
  applyLang(next);
  return next;
}

export function isLang(value: string): value is Lang {
  return (SUPPORTED as readonly string[]).includes(value);
}

export function t(lang: Lang): (typeof COPY)[Lang] {
  return COPY[lang];
}
